import { useState, useEffect } from 'react';
import {
  Wand2,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  X,
  Eye,
  Timer,
} from 'lucide-react';
import {
  transformLogs,
  workflows as workflowsApi,
  TransformLog,
  TransformLogFilters,
  TransformStats,
  AdminWorkflow,
} from '../../api/client';
import { formatDateTime, cn } from '../../lib/utils';

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof CheckCircle }> = {
  success: { label: 'Success', color: 'text-emerald-400', icon: CheckCircle },
  error: { label: 'Error', color: 'text-rose-400', icon: XCircle },
  timeout: { label: 'Timeout', color: 'text-amber-400', icon: Timer },
  fallback: { label: 'Fallback', color: 'text-orange-400', icon: AlertCircle },
};

export default function TransformLogs() {
  const [logs, setLogs] = useState<TransformLog[]>([]);
  const [workflowList, setWorkflowList] = useState<AdminWorkflow[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<TransformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<TransformLog | null>(null);

  const [filters, setFilters] = useState<TransformLogFilters>({
    limit: 50,
    offset: 0,
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = async () => {
    try {
      const [logsRes, statsRes, wfRes] = await Promise.all([
        transformLogs.list(filters),
        transformLogs.stats(),
        workflowsApi.list(),
      ]);
      setLogs(logsRes.logs);
      setTotal(logsRes.total);
      setStats(statsRes.stats);
      setWorkflowList(wfRes.workflows);
      setError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch transform logs';
      console.error('Failed to fetch transform logs:', error);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const handleFilterChange = (key: keyof TransformLogFilters, value: string | undefined) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
      offset: 0,
    }));
  };

  const handlePageChange = (direction: 'prev' | 'next') => {
    const newOffset = direction === 'prev'
      ? Math.max(0, (filters.offset || 0) - (filters.limit || 50))
      : (filters.offset || 0) + (filters.limit || 50);
    setFilters(prev => ({ ...prev, offset: newOffset }));
  };

  const currentPage = Math.floor((filters.offset || 0) / (filters.limit || 50)) + 1;
  const totalPages = Math.ceil(total / (filters.limit || 50));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading transform logs...</span>
        </div>
      </div>
    );
  }

  const successRate = stats && stats.total > 0
    ? Math.round((stats.success_count / stats.total) * 100)
    : null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient-violet">Prompt Transforms</h1>
          <p className="text-muted-foreground mt-1">LLM prompt rewriting history</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'btn-secondary px-4 py-2 rounded-xl flex items-center gap-2 text-sm',
              showFilters && 'bg-violet-500/20 text-violet-400'
            )}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
          <button
            onClick={fetchData}
            className="btn-secondary px-4 py-2 rounded-xl flex items-center gap-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards (24h) */}
      {stats && stats.total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-fade-in-up animate-delay-50">
          <div className="card-glow rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Total (24h)</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="card-glow rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Success Rate</p>
            <p className={cn(
              'text-2xl font-bold',
              successRate !== null && successRate >= 90 ? 'text-emerald-400' :
              successRate !== null && successRate >= 70 ? 'text-amber-400' : 'text-rose-400'
            )}>
              {successRate !== null ? `${successRate}%` : '-'}
            </p>
          </div>
          <div className="card-glow rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Avg Duration</p>
            <p className="text-2xl font-bold">
              {stats.avg_duration_ms ? `${(stats.avg_duration_ms / 1000).toFixed(1)}s` : '-'}
            </p>
          </div>
          <div className="card-glow rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Errors / Timeouts</p>
            <p className="text-2xl font-bold text-rose-400">
              {stats.error_count + stats.timeout_count}
            </p>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-5 animate-scale-in">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-rose-400" />
            <div className="flex-1">
              <p className="text-sm text-rose-300/80">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-rose-400 hover:text-rose-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="card-glow rounded-2xl p-6 animate-fade-in-up">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Workflow</label>
              <select
                value={filters.workflowSlug || ''}
                onChange={(e) => handleFilterChange('workflowSlug', e.target.value)}
                className="input-neural w-full px-3 py-2.5 rounded-lg text-sm appearance-none cursor-pointer"
              >
                <option value="">All Workflows</option>
                {workflowList.map(w => (
                  <option key={w.id} value={w.slug}>{w.name} ({w.slug})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Status</label>
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="input-neural w-full px-3 py-2.5 rounded-lg text-sm appearance-none cursor-pointer"
              >
                <option value="">All Statuses</option>
                <option value="success">Success</option>
                <option value="error">Error</option>
                <option value="timeout">Timeout</option>
                <option value="fallback">Fallback</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setFilters({ limit: 50, offset: 0 })}
              className="btn-secondary px-4 py-2 rounded-lg text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Logs Table */}
      <div className="card-glow rounded-2xl overflow-hidden animate-fade-in-up animate-delay-100">
        <table className="table-neural min-w-full">
          <thead>
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Time
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Workflow
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Duration
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Original
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Transformed
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log, index) => {
              const config = STATUS_CONFIG[log.status] || STATUS_CONFIG.error;
              const StatusIcon = config.icon;

              return (
                <tr
                  key={log.id}
                  className="animate-fade-in-up hover:bg-secondary/30 cursor-pointer transition-colors"
                  style={{ animationDelay: `${(index + 2) * 30}ms` }}
                  onClick={() => setSelectedLog(log)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      {formatDateTime(log.createdAt)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <code className="text-xs font-mono bg-secondary/50 px-2 py-1 rounded">
                      {log.workflowSlug}
                    </code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <StatusIcon className={cn('w-4 h-4', config.color)} />
                      <span className={cn('text-sm', config.color)}>
                        {config.label}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-mono">
                      {(log.durationMs / 1000).toFixed(2)}s
                    </span>
                  </td>
                  <td className="px-6 py-4 max-w-[200px]">
                    <p className="text-sm truncate text-muted-foreground" title={log.originalPrompt}>
                      {log.originalPrompt}
                    </p>
                  </td>
                  <td className="px-6 py-4 max-w-[200px]">
                    <p className="text-sm truncate" title={log.transformedPrompt || '-'}>
                      {log.transformedPrompt || <span className="text-muted-foreground">-</span>}
                    </p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLog(log);
                      }}
                      className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-violet-400 transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {logs.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center">
                      <Wand2 className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">No transform logs found</p>
                      <p className="text-sm text-muted-foreground/70">
                        Logs will appear here when prompts are transformed via the Prompt Transformer
                      </p>
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-border/50">
            <div className="text-sm text-muted-foreground">
              Showing {(filters.offset || 0) + 1} - {Math.min((filters.offset || 0) + (filters.limit || 50), total)} of {total}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange('prev')}
                disabled={currentPage === 1}
                className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange('next')}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <TransformDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
    </div>
  );
}

function TransformDetailModal({
  log,
  onClose,
}: {
  log: TransformLog;
  onClose: () => void;
}) {
  const config = STATUS_CONFIG[log.status] || STATUS_CONFIG.error;
  const StatusIcon = config.icon;

  return (
    <div className="fixed inset-0 modal-overlay flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="modal-content rounded-2xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center',
              log.status === 'success' ? 'bg-emerald-500/10' : 'bg-rose-500/10'
            )}>
              <StatusIcon className={cn('w-5 h-5', config.color)} />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Transform Details</h2>
              <p className="text-sm text-muted-foreground">{formatDateTime(log.createdAt)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-secondary/30">
            <p className="text-xs text-muted-foreground mb-1">Workflow</p>
            <code className="text-sm font-mono">{log.workflowSlug}</code>
          </div>
          <div className="p-4 rounded-xl bg-secondary/30">
            <p className="text-xs text-muted-foreground mb-1">Model</p>
            <code className="text-sm font-mono truncate block" title={log.model}>{log.model}</code>
          </div>
          <div className="p-4 rounded-xl bg-secondary/30">
            <p className="text-xs text-muted-foreground mb-1">Status</p>
            <div className="flex items-center gap-2">
              <StatusIcon className={cn('w-4 h-4', config.color)} />
              <span className={cn('text-sm font-medium', config.color)}>{config.label}</span>
            </div>
          </div>
          <div className="p-4 rounded-xl bg-secondary/30">
            <p className="text-xs text-muted-foreground mb-1">Duration</p>
            <p className="font-mono">{(log.durationMs / 1000).toFixed(2)}s</p>
          </div>
          <div className="p-4 rounded-xl bg-secondary/30">
            <p className="text-xs text-muted-foreground mb-1">Tokens (in / out)</p>
            <p className="font-mono">
              {log.inputTokens ?? '-'} / {log.outputTokens ?? '-'}
            </p>
          </div>
          <div className="p-4 rounded-xl bg-secondary/30">
            <p className="text-xs text-muted-foreground mb-1">Job ID</p>
            <p className="font-mono text-xs truncate" title={log.jobId}>{log.jobId}</p>
          </div>
        </div>

        {/* Error Message */}
        {log.errorMessage && (
          <div className="mb-6">
            <p className="text-sm text-muted-foreground mb-2">Error Message</p>
            <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20">
              <p className="text-sm text-rose-300">{log.errorMessage}</p>
            </div>
          </div>
        )}

        {/* Original Prompt */}
        <div className="mb-6">
          <p className="text-sm text-muted-foreground mb-2">Original Prompt</p>
          <pre className="p-4 rounded-xl bg-secondary/30 text-sm whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
            {log.originalPrompt}
          </pre>
        </div>

        {/* Transformed Prompt */}
        <div className="mb-6">
          <p className="text-sm text-muted-foreground mb-2">Transformed Prompt</p>
          {log.transformedPrompt ? (
            <pre className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/10 text-sm whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
              {log.transformedPrompt}
            </pre>
          ) : (
            <div className="p-4 rounded-xl bg-secondary/30 text-sm text-muted-foreground italic">
              No transformed prompt (original was used)
            </div>
          )}
        </div>

        {/* Close Button */}
        <div className="flex justify-end pt-4 border-t border-border">
          <button
            onClick={onClose}
            className="btn-secondary px-5 py-2.5 rounded-xl text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
